import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Bell, ChevronDown, User, LogOut, Settings, ArrowRight } from 'lucide-react';
import { notificationsAPI } from '../../services/api';
import { useAppContext } from '../../context/AppContext';
import MessageModal from '../../components/common/MessageModal';
import './Navbar.css';

const formatNotificationTime = (createdAt) => {
  if (!createdAt) {
    return 'Just now';
  }

  const notificationDate = new Date(createdAt);
  if (Number.isNaN(notificationDate.getTime())) {
    return 'Just now';
  }

  const diffMs = Date.now() - notificationDate.getTime();
  const diffMinutes = Math.max(1, Math.floor(diffMs / 60000));

  if (diffMinutes < 60) {
    return `${diffMinutes}m ago`;
  }

  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) {
    return `${diffHours}h ago`;
  }

  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) {
    return `${diffDays}d ago`;
  }

  return notificationDate.toLocaleDateString();
};

const getNotificationErrorMessage = (error, fallbackMessage) => {
  if (!error?.response || error.response.status >= 500) {
    return null;
  }

  return error.response?.data?.error || fallbackMessage;
};

const routeTitles = {
  '/': 'Dashboard',
  '/content-library': 'VR Library',
  '/modules': 'Modules',
  '/enrollment': 'User Enrollment',
  '/analytics': 'Analytics & Reports',
  '/settings': 'Settings',
  '/profile': 'My Profile',
};

const getPageTitle = (pathname) => {
  if (pathname.startsWith('/content-library/')) return 'VR App Details';
  if (pathname.startsWith('/modules/')) return 'Module Details';
  return routeTitles[pathname] || 'NLNG VR LMS';
};

const Navbar = () => {
  const { sidebarCollapsed, currentUser, logout, showNotification } = useAppContext();
  const [profileOpen, setProfileOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [messageModalOpen, setMessageModalOpen] = useState(false);
  const [messageRecipient, setMessageRecipient] = useState('');
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const profileRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  const pageTitle = getPageTitle(location.pathname);

  useEffect(() => {
    let isActive = true;

    const loadNotifications = async () => {
      if (!currentUser?.id) {
        if (isActive) {
          setNotifications([]);
          setUnreadCount(0);
          setNotificationsLoading(false);
        }
        return;
      }

      setNotificationsLoading(true);

      try {
        const response = await notificationsAPI.getNotifications({ limit: 20 });
        const notificationData = response.data?.data;

        if (!isActive) {
          return;
        }

        setNotifications(notificationData?.items || []);
        setUnreadCount(notificationData?.unreadCount || 0);
      } catch (error) {
        if (!isActive) {
          return;
        }

        const message = getNotificationErrorMessage(error, 'Failed to load notifications');
        if (message) {
          showNotification(message, 'error');
        }
      } finally {
        if (isActive) {
          setNotificationsLoading(false);
        }
      }
    };

    loadNotifications();

    return () => {
      isActive = false;
    };
  }, [currentUser?.id, showNotification]);

  useEffect(() => {
    if (!notifOpen || !currentUser?.id) {
      return undefined;
    }

    let isActive = true;

    const refreshNotifications = async () => {
      try {
        const response = await notificationsAPI.getNotifications({ limit: 20 });
        const notificationData = response.data?.data;

        if (!isActive) {
          return;
        }

        setNotifications(notificationData?.items || []);
        setUnreadCount(notificationData?.unreadCount || 0);
      } catch (error) {
        if (!isActive) {
          return;
        }

        const message = getNotificationErrorMessage(error, 'Failed to refresh notifications');
        if (message) {
          showNotification(message, 'error');
        }
      }
    };

    refreshNotifications();

    return () => {
      isActive = false;
    };
  }, [notifOpen, currentUser?.id, showNotification]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setProfileOpen(false);
        setNotifOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleEmailClick = (email) => {
    setMessageRecipient(email);
    setMessageModalOpen(true);
  };

  const handleSendMessage = ({ recipientEmail, subject, message }) => {
    // In a real app, you would send the message via API
    showNotification(`Message sent to ${recipientEmail} successfully!`, 'success');
    setMessageModalOpen(false);
  };

  const markNotificationAsRead = async (notificationId) => {
    const targetNotification = notifications.find((notification) => notification.id === notificationId);
    if (!targetNotification || targetNotification.isRead) {
      return;
    }

    try {
      const response = await notificationsAPI.markAsRead(notificationId);
      const nextUnreadCount = response.data?.data?.unreadCount;

      setNotifications((currentNotifications) => currentNotifications.map((notification) => (
        notification.id === notificationId
          ? {
              ...notification,
              isRead: true,
              readAt: response.data?.data?.notification?.readAt || notification.readAt,
            }
          : notification
      )));
      setUnreadCount(typeof nextUnreadCount === 'number' ? nextUnreadCount : Math.max(0, unreadCount - 1));
    } catch (error) {
      const message = getNotificationErrorMessage(error, 'Failed to update notification');
      if (message) {
        showNotification(message, 'error');
      }
    }
  };

  const markAllNotificationsAsRead = async () => {
    if (unreadCount === 0) {
      return;
    }

    try {
      await notificationsAPI.markAllAsRead();
      setNotifications((currentNotifications) => currentNotifications.map((notification) => ({
        ...notification,
        isRead: true,
      })));
      setUnreadCount(0);
    } catch (error) {
      const message = getNotificationErrorMessage(error, 'Failed to update notifications');
      if (message) {
        showNotification(message, 'error');
      }
    }
  };

  return (
    <header
      className="navbar"
      style={{
        left: sidebarCollapsed
          ? 'var(--sidebar-collapsed-width)'
          : 'var(--sidebar-width)',
      }}
    >
      {/* Left: Page Title */}
      <div className="navbar__left">
        <div className="navbar__page-title">
          <h2 className="navbar__title">{pageTitle}</h2>
          <span className="navbar__breadcrumb">NLNG VR LMS</span>
        </div>
      </div>

      {/* Right: Actions */}
      <div className="navbar__right" ref={profileRef}>
        {/* Notifications */}
        <div className="navbar__notif-wrapper">
          <button
            className="navbar__icon-btn"
            onClick={() => {
              setNotifOpen((p) => !p);
              setProfileOpen(false);
            }}
          >
            <Bell size={19} strokeWidth={1.8} />
            {unreadCount > 0 && (
              <span className="navbar__badge">{unreadCount}</span>
            )}
          </button>

          {notifOpen && (
            <div className="navbar__dropdown navbar__notif-dropdown">
              <div className="navbar__dropdown-header">
                <span>Notifications</span>
                {unreadCount > 0 ? (
                  <button
                    type="button"
                    className="navbar__notif-action"
                    onClick={markAllNotificationsAsRead}
                  >
                    Mark all as read
                  </button>
                ) : (
                  <span className="navbar__badge-pill">All caught up</span>
                )}
              </div>
              <div className="navbar__notif-list">
                {notificationsLoading && notifications.length === 0 ? (
                  <div className="navbar__notif-empty">
                    <p className="navbar__notif-empty-title">Loading notifications</p>
                    <span className="navbar__notif-time">Checking for your latest updates.</span>
                  </div>
                ) : notifications.length === 0 ? (
                  <div className="navbar__notif-empty">
                    <p className="navbar__notif-empty-title">No notifications</p>
                    <span className="navbar__notif-time">You have no updates right now.</span>
                  </div>
                ) : notifications.map((n) => (
                  <div
                    key={n.id}
                    className={`navbar__notif-item ${!n.isRead ? 'navbar__notif-item--unread' : ''}`}
                    onClick={() => markNotificationAsRead(n.id)}
                  >
                    <div className="navbar__notif-dot" />
                    <div className="navbar__notif-content">
                      <p className="navbar__notif-text">{n.title}</p>
                      <p className="navbar__notif-subtext">{n.message}</p>
                      <span className="navbar__notif-time">{formatNotificationTime(n.createdAt)}</span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="navbar__notif-footer">
                <button
                  type="button"
                  className="navbar__notif-view-all"
                  onClick={() => {
                    navigate('/notifications');
                    setNotifOpen(false);
                  }}
                >
                  <span>View all notifications</span>
                  <ArrowRight size={14} />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Divider */}
        <div className="navbar__divider" />

        {/* Profile */}
        <div className="navbar__profile-wrapper">
          <button
            className="navbar__profile-btn"
            onClick={() => {
              setProfileOpen((p) => !p);
              setNotifOpen(false);
            }}
          >
            <div className="navbar__avatar">
              {currentUser.avatar ? (
                <img src={currentUser.avatar} alt={currentUser.name} />
              ) : (
                <span>{currentUser.initials}</span>
              )}
            </div>
            <div className="navbar__profile-info">
              <span className="navbar__profile-name">{currentUser.name}</span>
              <span className="navbar__profile-role">{currentUser.role}</span>
            </div>
            <ChevronDown
              size={14}
              className={`navbar__chevron ${profileOpen ? 'navbar__chevron--open' : ''}`}
            />
          </button>

          {profileOpen && (
            <div className="navbar__dropdown navbar__profile-dropdown">
              <div className="navbar__dropdown-user">
                <div className="navbar__avatar navbar__avatar--lg">
                  {currentUser.avatar ? (
                    <img src={currentUser.avatar} alt={currentUser.name} />
                  ) : (
                    <span>{currentUser.initials}</span>
                  )}
                </div>
                <div>
                  <p className="navbar__dropdown-name">{currentUser.name}</p>
                  <button
                    className="navbar__dropdown-email link"
                    onClick={() => handleEmailClick(currentUser.email)}
                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left' }}
                  >
                    {currentUser.email}
                  </button>
                </div>
              </div>
              <div className="navbar__dropdown-divider" />
              <button
                className="navbar__dropdown-item"
                onClick={() => {
                  navigate('/profile');
                  setProfileOpen(false);
                }}
              >
                <User size={15} />
                <span>My Profile</span>
              </button>
              {currentUser.role !== 'Trainee' && (
                <button
                  className="navbar__dropdown-item"
                  onClick={() => {
                    navigate('/settings');
                    setProfileOpen(false);
                  }}
                >
                  <Settings size={15} />
                  <span>Settings</span>
                </button>
              )}
              <div className="navbar__dropdown-divider" />
              <button
                className="navbar__dropdown-item navbar__dropdown-item--danger"
                onClick={() => {
                  logout();
                  setProfileOpen(false);
                  navigate('/login');
                }}
              >
                <LogOut size={15} />
                <span>Sign Out</span>
              </button>
            </div>
          )}
        </div>
      </div>
      <MessageModal
        isOpen={messageModalOpen}
        onClose={() => setMessageModalOpen(false)}
        recipientEmail={messageRecipient}
        currentUserRole={currentUser?.role}
        onSend={handleSendMessage}
      />
    </header>
  );
};

export default Navbar;
